/**
 * Loads the inventory for a database, falling back to the offline cache.
 *
 * Every screen that shows items needs the same three things: the current
 * database, the items in it, and an honest signal about whether what is on
 * screen came from the server or from a cache that may be hours old. Selling
 * from stale stock is a real risk at a convention, so `stale` is surfaced
 * rather than hidden.
 */

import { useCallback, useEffect, useState } from 'react';

import * as api from './api';
import { cacheGet, cacheSet } from './offline';

interface InventoryState {
  db: string;
  setDb: (db: string) => void;
  databases: string[];
  items: Record<string, api.Item>;
  loading: boolean;
  /** True when the items shown came from the offline cache. */
  stale: boolean;
  error: string;
  reload: () => void;
}

const DEFAULT_DB = 'databases/db.json';
const DB_PREFERENCE_KEY = 'cpos.db';

export function useInventory(): InventoryState {
  const [db, setDbState] = useState<string>(
    () => localStorage.getItem(DB_PREFERENCE_KEY) ?? DEFAULT_DB,
  );
  const [databases, setDatabases] = useState<string[]>([DEFAULT_DB]);
  const [items, setItems] = useState<Record<string, api.Item>>({});
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState('');
  const [nonce, setNonce] = useState(0);

  const setDb = useCallback((next: string) => {
    setDbState(next);
    try {
      localStorage.setItem(DB_PREFERENCE_KEY, next);
    } catch {
      // A blocked localStorage only costs the remembered choice.
    }
  }, []);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const data = await api.listDatabases();
        if (cancelled) return;
        const names = data.databases.map((entry) => entry.db_path).filter(Boolean);
        if (names.length) setDatabases(names);
      } catch {
        // Offline: keep whatever list we already had.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [nonce]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');

    void (async () => {
      const cacheKey = `items:${db}`;
      try {
        const data = await api.listItems(db);
        if (cancelled) return;
        setItems(data.items ?? {});
        setStale(false);
        void cacheSet(cacheKey, data.items ?? {});
      } catch (err) {
        if (cancelled) return;
        const apiError = err as api.ApiError;
        const cached = await cacheGet<Record<string, api.Item>>(cacheKey);
        if (cancelled) return;
        if (cached) {
          setItems(cached);
          setStale(true);
          setError('');
        } else {
          setItems({});
          setStale(false);
          setError(
            apiError.isOffline
              ? 'Offline, and this database has not been loaded on this device yet.'
              : apiError.message,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, nonce]);

  return { db, setDb, databases, items, loading, stale, error, reload };
}
